import { motion } from 'framer-motion';
import {
    Award,
    BookOpen,
    Building2,
    FileCheck,
    Globe,
    Lightbulb,
    Scale,
    Shield,
    TrendingUp,
    Users
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from './ui/card';

const Welcome = () => {
    const navigate = useNavigate();

    const services = [
        {
            icon: Lightbulb,
            title: '智能',
            description: '賦能未來，透過AI與數據分析驅動企業決策與效率提升',
            color: 'from-blue-500 to-cyan-500',
            path: '/patent-search'
        },
        {
            icon: Globe,
            title: '雲端',
            description: '打造彈性雲端架構加速您的業務創新，隨時滿足企業成長需求',
            color: 'from-purple-500 to-pink-500',
            path: '/case-management'
        },
        {
            icon: Shield,
            title: '資安',
            description: '全方位防護，確保數據與業務運行的安全無虞',
            color: 'from-green-500 to-emerald-500',
            path: '/documents'
        },
        {
            icon: TrendingUp,
            title: '虛擬應用',
            description: '提供創新解決方案，實現虛擬環境中的真實生產力',
            color: 'from-orange-500 to-red-500',
            path: '/legal-chat'
        }
    ];

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
            {/* Hero Section */}
            <div className="relative overflow-hidden">
                {/* 背景裝飾 */}
                <div className="absolute inset-0 overflow-hidden">
                    <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-400 dark:bg-blue-600 rounded-full mix-blend-multiply dark:mix-blend-soft-light filter blur-xl opacity-20 animate-blob"></div>
                    <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-400 dark:bg-purple-600 rounded-full mix-blend-multiply dark:mix-blend-soft-light filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
                    <div className="absolute top-40 left-40 w-80 h-80 bg-pink-400 dark:bg-pink-600 rounded-full mix-blend-multiply dark:mix-blend-soft-light filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
                </div>

                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        className="text-center"
                    >
                        {/* Logo 區域 */}
                        <motion.div
                            initial={{ scale: 0.5, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ duration: 0.5 }}
                            className="mb-8"
                        >
                            <div className="inline-block bg-white rounded-2xl p-6 shadow-2xl">
                                <img 
                                    src="https://www.cloudinfo.com.tw/uploads/57a15888-8c0b-4dce-b8cf-5d332f2c1b7e-logo.png" 
                                    alt="云碩科技 Logo" 
                                    className="h-20"
                                />
                            </div>
                        </motion.div>

                        <p className="text-xl md:text-2xl text-gray-600 dark:text-gray-300 mb-4">
                            智慧財產權 AI 助理平台
                        </p>

                        <p className="text-lg text-gray-500 dark:text-gray-400 max-w-3xl mx-auto mb-12">
                            資訊服務 + 雲端系統整合的智造業
                            <br />
                            專注於雲端、資安、虛擬應用與智能四大技術方向
                        </p>
                    </motion.div>

                    {/* 主要服務卡片 */}
                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4, duration: 0.8 }}
                        className="mt-16"
                    >
                        <h2 className="text-3xl font-bold text-center text-gray-900 dark:text-white mb-8">
                            核心服務
                        </h2>
                        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                            {services.map((service, index) => (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ delay: index * 0.1 + 0.5 }}
                                    whileHover={{ scale: 1.05, y: -5 }}
                                    className="cursor-pointer"
                                    onClick={() => navigate(service.path)}
                                >
                                    <Card className="h-full bg-white dark:bg-gray-800 border-2 border-transparent hover:border-blue-500 dark:hover:border-blue-400 transition-all duration-300 shadow-lg hover:shadow-2xl">
                                        <CardContent className="p-6">
                                            <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${service.color} flex items-center justify-center mb-4 shadow-lg`}>
                                                <service.icon className="w-8 h-8 text-white" />
                                            </div>
                                            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                                                {service.title}
                                            </h3>
                                            <p className="text-gray-600 dark:text-gray-400 text-sm">
                                                {service.description}
                                            </p>
                                        </CardContent>
                                    </Card>
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>

                    {/* 服務據點 */}
                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1.2, duration: 0.8 }}
                        className="mt-20 mb-12"
                    >
                        <Card className="bg-gradient-to-r from-blue-600 to-purple-600 border-0 shadow-2xl">
                            <CardContent className="p-8 md:p-12 text-center text-white">
                                <Users className="w-16 h-16 mx-auto mb-6 opacity-90" />
                                <h2 className="text-3xl font-bold mb-4">全台服務據點</h2>
                                <div className="space-y-2 text-lg mb-6 opacity-90">
                                    <p>台北服務處 | TEL: 02-2585-8655</p>
                                    <p>台中服務處 | TEL: 04-2247-9035</p>
                                    <p>高雄服務處 | TEL: 02-2585-8655</p>
                                </div>
                                <div className="mt-6 text-base opacity-80">
                                    <p>信箱：service@cloudinfo.com.tw</p>
                                    <p className="mt-2">官網：www.cloudinfo.com.tw</p>
                                </div>
                                <div className="mt-8 inline-flex items-center justify-center px-6 py-2 bg-white/20 rounded-full text-sm">
                                    <Award className="w-4 h-4 mr-2" />
                                    ISO 27001:2022 資安認證 | AI技術服務機構能量登錄
                                </div>
                            </CardContent>
                        </Card>
                    </motion.div>
                </div>
            </div>

            {/* 添加動畫樣式 */}
            <style jsx>{`
        @keyframes blob {
          0% {
            transform: translate(0px, 0px) scale(1);
          }
          33% {
            transform: translate(30px, -50px) scale(1.1);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.9);
          }
          100% {
            transform: translate(0px, 0px) scale(1);
          }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
        </div>
    );
};

export default Welcome;
